import { useCallback, useEffect, useState } from 'react';

import { Tabs } from '@bambi/component-library/src/components/Tabs/Tabs';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  GeoLocationRead,
  useLocationsGeolocationsListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal';
import {
  GeolocationFormContent,
  geolocationModalHeight,
  geolocationModalWidth,
} from 'src/features/settings/geolocations/GeolocationForm/GeolocationForm';
import { setGeolocationSearchTerm } from 'src/features/settings/setting.slice';

import { GeolocationButton } from './GeolocationButton';
import { GeolocationPickerList } from './GeolocationPickerList';

interface GeolocationPickerProps {
  onSelect: (selectedGeolocation: GeoLocationRead) => void;
}

export function GeolocationPicker({ onSelect }: GeolocationPickerProps) {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const closePicker = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    // Reset the search term when the modal mounts
    // Allow it to persist when switching modal tabs
    dispatch(setGeolocationSearchTerm(''));
  }, [dispatch]);

  const defaultTab = 'select';
  const { tabOverride } = useTabOverride();

  return (
    <>
      <GeolocationButton onClick={() => setIsOpen(true)} />
      <Modal
        open={isOpen}
        setOpen={setIsOpen}
        onClose={closePicker}
        title="Select Geolocation"
      >
        <div className={`overflow-hidden ${geolocationModalWidth}`}>
          <Tabs
            tabs={[
              {
                label: 'Select Geolocation',
                value: 'select',
                content: (
                  <TabContentWrapper enableScrolling>
                    <GeolocationPickerList
                      onSelect={(selectedGeolocation) => {
                        onSelect(selectedGeolocation);
                        closePicker();
                      }}
                    />
                  </TabContentWrapper>
                ),
              },
              {
                label: 'Add Geolocation',
                value: 'add',
                content: (
                  <TabContentWrapper>
                    <GeolocationFormContent
                      selectedGeolocation={undefined}
                      onDismiss={closePicker}
                      onCreateUpdate={(selectedGeolocation) => {
                        onSelect(selectedGeolocation);
                        closePicker();
                      }}
                    />
                  </TabContentWrapper>
                ),
              },
            ]}
            defaultValue={tabOverride ?? defaultTab}
            tabListStyle="justify-center"
          />
        </div>
      </Modal>
    </>
  );
}

interface TabContentWrapperProps {
  children: React.ReactNode;
  enableScrolling?: boolean;
}

function TabContentWrapper({
  children,
  enableScrolling = false,
}: TabContentWrapperProps) {
  return (
    <div
      className={`${geolocationModalHeight} ${
        enableScrolling ? 'overflow-y-auto' : ''
      }`}
    >
      {children}
    </div>
  );
}

/**
 * Show the "Add Geolocation" tab if there are no geolocations.
 */
const useTabOverride = () => {
  const [tabOverride, setTabOverride] = useState<'select' | 'add'>();

  const geolocationsData = useLocationsGeolocationsListQuery(
    {
      page: 1,
      pageSize: 1,
    },
    {}
  );

  useEffect(() => {
    // Wait until the request is complete
    if (geolocationsData.data) {
      const initialTab = geolocationsData.data.count > 0 ? 'select' : 'add';
      setTabOverride(initialTab);
    }
  }, [geolocationsData.data]);

  return { tabOverride };
};
